/**MTNSdgImpact.js — SDG Impact & Development Orchestration — Action-oriented */
import React, { useState, useEffect } from "react";
import { useMTN } from "@/context/MTNContext";
import client from "@/api/client";
import { AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";

const fmtU=v=>{if(!v&&v!==0)return"—";const n=Number(v);if(n>=1e9)return`$${(n/1e9).toFixed(1)}B`;if(n>=1e6)return`$${(n/1e6).toFixed(1)}M`;if(n>=1e3)return`$${(n/1e3).toFixed(0)}K`;return`$${n}`;};

export default function MTNSdgImpact() {
  const { loading, selectedPeriod } = useMTN();
  const [sdgData, setSdgData] = useState(null);
  const [loadingSDG, setLoadingSDG] = useState(false);
  const [activeSDG, setActiveSDG] = useState(null);

  useEffect(() => {
    if (!selectedPeriod) return;
    setLoadingSDG(true);
    client.get(`/mtn/sdg/${selectedPeriod}`)
      .then(r => setSdgData(r.data))
      .catch(() => {})
      .finally(() => setLoadingSDG(false));
  }, [selectedPeriod]);

  if (loading || loadingSDG) return <div style={{padding:40,color:"#64748b",fontSize:12}}>Loading SDG intelligence…</div>;
  if (!sdgData) return <div style={{padding:40,color:"#64748b",fontSize:12}}>No SDG data available.</div>;

  const sdgList = sdgData.sdg_intelligence || [];
  const rio = sdgData.rio_markers || [];

  return (
    <div style={{padding:"24px 28px"}}>
      <div style={{marginBottom:20}}>
        <span style={{fontSize:9,fontWeight:800,color:"#FFCB00",letterSpacing:".08em",textTransform:"uppercase",background:"#FFCB0015",border:"1px solid #FFCB0030",padding:"2px 8px",borderRadius:4}}>MTN Nigeria</span>
        <h1 style={{fontSize:20,fontWeight:900,color:"#f1f5f9",marginTop:6}}>SDG Impact & Development Orchestration</h1>
        <p style={{fontSize:11,color:"#475569"}}>Action-oriented SDG intelligence · Evidence quality · Finance linkage · Required actions</p>
      </div>

      {/* Headline metrics */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:16}}>
        {[["SDG Headline Score",`${sdgData.sdg_headline_score||0}/100`,"#FFCB00"],
          ["SDGs Tracked",`${sdgList.length}`,"#60a5fa"],
          ["Total SDG Investment",fmtU(sdgData.total_sdg_investment_usd),"#22c55e"],
          ["Evidence Coverage",`${sdgData.evidence_coverage||0}/100`,"#a78bfa"],
        ].map(([l,v,c])=>(
          <div key={l} style={{background:"#0d1423",border:`1px solid ${c}25`,borderRadius:8,padding:"12px 14px"}} data-testid={`sdg-metric-${l.toLowerCase().replace(/\s+/g,"-")}`}>
            <p style={{fontSize:8,color:"#475569",textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>{l}</p>
            <p style={{fontSize:18,fontWeight:900,color:c}}>{v}</p>
          </div>
        ))}
      </div>

      {/* SDG Heatmap */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16,marginBottom:14}} data-testid="sdg-heatmap">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>SDG Contribution Heatmap — Click to drill down</p>
        <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
          {sdgList.map(s=>(
            <button key={s.sdg} onClick={()=>setActiveSDG(activeSDG===s.sdg?null:s.sdg)}
              data-testid={`sdg-tile-${s.sdg.replace(" ","-")}`}
              style={{background:activeSDG===s.sdg?s.color:s.color+"20",border:`2px solid ${s.color}${activeSDG===s.sdg?"":"60"}`,
                borderRadius:8,padding:"10px 14px",cursor:"pointer",minWidth:110,textAlign:"left",transition:"all .15s"}}>
              <p style={{fontSize:10,fontWeight:800,color:s.color,marginBottom:2}}>{s.sdg}</p>
              <p style={{fontSize:9,color:activeSDG===s.sdg?"#fff":"#94a3b8",marginBottom:3}}>{s.label}</p>
              <div style={{height:4,background:"rgba(255,255,255,.1)",borderRadius:2,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${s.score}%`,background:activeSDG===s.sdg?"rgba(255,255,255,.6)":s.color,borderRadius:2}}/>
              </div>
              <p style={{fontSize:8,color:activeSDG===s.sdg?"rgba(255,255,255,.7)":s.color,marginTop:2}}>{s.score}/100 · {s.evidence_quality}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Drill-down panel */}
      {activeSDG && (() => {
        const s = sdgList.find(x=>x.sdg===activeSDG);
        if (!s) return null;
        return (
          <div style={{background:"#0d1423",border:`1px solid ${s.color}40`,borderRadius:8,padding:16,marginBottom:14}} data-testid="sdg-drilldown">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:12}}>
              <div>
                <p style={{fontSize:13,fontWeight:900,color:s.color}}>{s.sdg} — {s.label}</p>
                <p style={{fontSize:10,color:"#94a3b8",marginTop:2}}>{s.telecom_angle}</p>
              </div>
              <div style={{textAlign:"right"}}>
                <p style={{fontSize:18,fontWeight:900,color:s.color}}>{s.score}/100</p>
                <p style={{fontSize:9,color:s.evidence_quality==="High"?"#22c55e":s.evidence_quality==="Medium"?"#FFCB00":"#ef4444"}}>Evidence: {s.evidence_quality}</p>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div style={{background:"#060c1a",borderRadius:6,padding:12}}>
                <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:4}}>Contribution</p>
                <p style={{fontSize:10,color:"#94a3b8"}}>{s.contribution_note}</p>
              </div>
              <div style={{background:"#060c1a",borderRadius:6,padding:12}}>
                <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:4}}>Finance Linkage</p>
                <p style={{fontSize:10,color:"#60a5fa"}}>{s.finance_link}</p>
              </div>
              <div style={{background:"#060c1a",borderRadius:6,padding:12}}>
                <p style={{fontSize:8,fontWeight:700,color:"#475569",textTransform:"uppercase",marginBottom:4}}>CAPEX Implication</p>
                <p style={{fontSize:10,color:"#f1f5f9"}}>{s.capex_note}</p>
              </div>
              <div style={{background:"#FFCB0015",border:"1px solid #FFCB0030",borderRadius:6,padding:12}}>
                <p style={{fontSize:8,fontWeight:700,color:"#FFCB00",textTransform:"uppercase",marginBottom:4}}>Next Required Action</p>
                <div style={{display:"flex",gap:5,alignItems:"flex-start"}}>
                  <ArrowRight size={12} color="#FFCB00" style={{flexShrink:0,marginTop:1}}/>
                  <p style={{fontSize:10,color:"#f1f5f9",fontWeight:600}}>{s.next_action}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* OECD Rio Markers */}
      <div style={{background:"#0d1423",border:"1px solid #1e293b",borderRadius:8,padding:16}} data-testid="rio-markers">
        <p style={{fontSize:9,fontWeight:700,color:"#94a3b8",textTransform:"uppercase",letterSpacing:".06em",marginBottom:12}}>OECD Rio Marker Alignment</p>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
          {rio.map(r=>(
            <div key={r.marker} style={{background:"#060c1a",borderRadius:6,padding:12}}>
              <p style={{fontSize:9,fontWeight:700,color:"#f1f5f9",marginBottom:3}}>{r.marker}</p>
              <div style={{height:6,background:"#1e293b",borderRadius:3,overflow:"hidden",marginBottom:4}}>
                <div style={{height:"100%",width:`${r.score}%`,background:r.score>=60?"#22c55e":r.score>=30?"#FFCB00":"#475569",borderRadius:3}}/>
              </div>
              <p style={{fontSize:8,fontWeight:700,color:r.score>=60?"#22c55e":r.score>=30?"#FFCB00":"#64748b"}}>{r.rating} · {r.score}/100</p>
              <p style={{fontSize:8,color:"#475569",marginTop:3}}>{r.note}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
